import type { TraceFilters, TraceFrame } from './types';

export const MAX_FILE_SIZE = 64 * 1024 * 1024;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return 'UNKNOWN';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${units[index]}`;
}

export function isVectorBlfHeader(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x4c && bytes[1] === 0x4f && bytes[2] === 0x47 && bytes[3] === 0x47;
}

export function filterFrames(frames: TraceFrame[], filters: TraceFilters): TraceFrame[] {
  const query = filters.query.trim().toUpperCase().replace(/^0X/, '');
  return frames.filter((frame) => {
    if (filters.channel !== 'all' && String(frame.channel) !== filters.channel) return false;
    if (filters.frameType !== 'all' && frame.frame_type !== filters.frameType) return false;
    if (filters.direction !== 'all' && frame.direction !== filters.direction) return false;
    if (!query) return true;
    const searchable = `${frame.frame_id_hex} ${frame.data_hex} ${frame.frame_type} ${frame.channel}`.toUpperCase();
    return searchable.includes(query);
  });
}

function csvCell(value: string | number | boolean): string {
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function buildCsv(frames: TraceFrame[]): string {
  const fields: (keyof TraceFrame)[] = [
    'index',
    'timestamp_s',
    'channel',
    'frame_id',
    'frame_id_hex',
    'direction',
    'frame_type',
    'is_extended_id',
    'is_remote_frame',
    'bitrate_switch',
    'error_state_indicator',
    'dlc_code',
    'data_length',
    'data_hex',
  ];
  const rows = frames.map((frame) => fields.map((field) => csvCell(frame[field])).join(','));
  return `${fields.join(',')}\n${rows.join('\n')}\n`;
}

function ascId(frame: TraceFrame): string {
  return `${frame.frame_id_hex}${frame.is_extended_id ? 'x' : ''}`;
}

function ascFdLine(frame: TraceFrame): string {
  const brs = frame.bitrate_switch ? 1 : 0;
  const esi = frame.error_state_indicator ? 1 : 0;
  const flags = 0x1000 | (brs ? 0x2000 : 0) | (esi ? 0x4000 : 0);
  const data = frame.data_hex;
  return [
    'CANFD',
    String(frame.channel).padStart(3),
    frame.direction.padEnd(4),
    ascId(frame).padStart(8),
    ''.padStart(34),
    brs,
    esi,
    frame.dlc_code.toString(16),
    String(frame.data_length).padStart(2),
    data,
    String(0).padStart(8),
    String(0).padStart(4),
    flags.toString(16).toUpperCase().padStart(8),
    ...Array(5).fill(String(0).padStart(8)),
  ].join(' ');
}

function ascDate(isoTimestamp?: string | null): string {
  const fallback = 'Thu Jan 01 00:00:00.000 1970';
  if (!isoTimestamp) return fallback;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/.exec(isoTimestamp);
  if (!match) return fallback;
  const [, year, month, day, hour, minute, second, millisecond] = match;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = Number(month) - 1;
  const weekday = new Date(Date.UTC(Number(year), monthIndex, Number(day))).getUTCDay();
  if (monthIndex < 0 || monthIndex > 11 || !Number.isFinite(weekday)) return fallback;
  return `${dayNames[weekday]} ${monthNames[monthIndex]} ${day} ${hour}:${minute}:${second}.${millisecond} ${year}`;
}

export function buildAsc(frames: TraceFrame[], measurementStart?: string | null): string {
  const headerDate = ascDate(measurementStart);
  const lines = [
    `date ${headerDate}`,
    'base hex  timestamps absolute',
    'internal events logged',
    `Begin Triggerblock ${headerDate}`,
    ' 0.000000 Start of measurement',
  ];

  for (const frame of frames) {
    const timestamp = Math.max(0, frame.timestamp_s).toFixed(6).padStart(9);
    let payload: string;
    if (frame.frame_type.endsWith('ERROR')) {
      payload = `${frame.channel}  ErrorFrame`;
    } else if (frame.frame_type === 'CAN_FD') {
      payload = ascFdLine(frame);
    } else if (frame.is_remote_frame) {
      payload = `${frame.channel}  ${ascId(frame).padEnd(15)} ${frame.direction.padEnd(4)} r ${frame.dlc_code.toString(16)}`;
    } else {
      payload = `${frame.channel}  ${ascId(frame).padEnd(15)} ${frame.direction.padEnd(4)} d ${frame.dlc_code.toString(16)} ${frame.data_hex}`.trimEnd();
    }
    lines.push(`${timestamp} ${payload}`);
  }
  lines.push('End TriggerBlock');
  return `${lines.join('\n')}\n`;
}

export function safeBaseName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.blf$/i, '');
  return (withoutExtension.replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '') || 'trace').slice(0, 100);
}

export function downloadText(fileName: string, content: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
